import React from 'react';
import { useRealtorContext, CommissionRecord, Realtor } from '../context/RealtorContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Section from '../components/layout/Section';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import FormInput from '../components/ui/FormInput';
import FormSelect from '../components/ui/FormSelect';
import FormTextarea from '../components/ui/FormTextarea';
import FileUpload from '../components/ui/FileUpload';
import { FaChevronDown } from 'react-icons/fa';

// ViewToggle Component
const ViewToggle: React.FC<{
  showCardView: boolean;
  setShowCardView: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ showCardView, setShowCardView }) => {
  return (
    <div className="flex justify-end mb-6">
      <div className="relative bg-gradient-to-r from-blue-50 to-indigo-50 p-2 rounded-2xl shadow-inner border border-blue-100">
        <div className="flex relative bg-white rounded-xl p-1 shadow-sm">
          <button
            className={`relative px-8 py-4 rounded-xl text-sm font-semibold transition-all duration-500 ease-out transform ${
              showCardView
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg scale-105 z-10'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
            onClick={() => setShowCardView(true)}
          >
            <span className="relative z-10 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Card View
            </span>
          </button>
          <button
            className={`relative px-8 py-4 rounded-xl text-sm font-semibold transition-all duration-500 ease-out transform ${
              !showCardView
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg scale-105 z-10'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
            onClick={() => setShowCardView(false)}
          >
            <span className="relative z-10 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              Table View
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

 const statusColors: Record<string, string> = {
    paid: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    cancelled: 'bg-red-100 text-red-800',
  };

// TableView Component
const TableView: React.FC<{ commissions: CommissionRecord[]; search: string; staticFilter: 'all' | 'paid' | 'pending' | 'cancelled' }> = ({ commissions, search, staticFilter }) => {
 
  return (
    <div className="overflow-x-auto">
      <div className="min-w-full divide-y divide-gray-200">
        {commissions
          .filter(c =>
            (staticFilter === 'all' || c.status === staticFilter) &&
            (c.propertyTitle.toLowerCase().includes(search.toLowerCase()) ||
            c.realtorName.toLowerCase().includes(search.toLowerCase()) ||
            (c.downlinerName?.toLowerCase() || '').includes(search.toLowerCase()) ||
            c.id.toLowerCase().includes(search.toLowerCase()))
          )
          .map((commission) => (
            <details key={commission.id} className="group border rounded-lg mb-4 bg-white shadow transition hover:shadow-lg">
              <summary className="flex flex-col md:flex-row md:items-center md:justify-between cursor-pointer px-4 py-3">
                <div className="flex flex-col md:flex-row md:items-center gap-2 w-full">
                  <span className="font-semibold text-primary">{commission.propertyTitle}</span>
                  <span className="text-xs text-gray-500">ID: {commission.propertyId}</span>
                  <span className="text-xs text-gray-500">Date: {new Date(commission.date).toLocaleDateString()}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[commission.status]}`}>
                    {commission.status.charAt(0).toUpperCase() + commission.status.slice(1)}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-2 md:mt-0">
                  <span className="text-lg font-bold text-green-700">₦{commission.amount.toLocaleString()}</span>
                  <svg className="text-gray-400 group-open:rotate-180 transition-transform duration-200" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </summary>
              <div className="px-4 py-4 border-t bg-gray-50 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="mb-2"><span className="font-medium text-gray-700">Realtor:</span> {commission.realtorName}</div>
                  <div className="mb-2"><span className="font-medium text-gray-700">Downliner:</span> {commission.downlinerName || 'N/A'}</div>
                  <div className="mb-2"><span className="font-medium text-gray-700">Type:</span> {commission.commissionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                </div>
                <div>
                  <div className="mb-2"><span className="font-medium text-gray-700">Property ID:</span> {commission.propertyId}</div>
                  <div className="mb-2"><span className="font-medium text-gray-700">Amount:</span> ₦{commission.amount.toLocaleString()}</div>
                  <div className="mb-2"><span className="font-medium text-gray-700">Date:</span> {new Date(commission.date).toLocaleDateString()}</div>
                  <div className="mb-2"><span className="font-medium text-gray-700">Status:</span> <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[commission.status]}`}>{commission.status.charAt(0).toUpperCase() + commission.status.slice(1)}</span></div>
                </div>
              </div>
            </details>
          ))}
        {commissions.filter(c =>
          (staticFilter === 'all' || c.status === staticFilter) &&
          (c.propertyTitle.toLowerCase().includes(search.toLowerCase()) ||
          c.realtorName.toLowerCase().includes(search.toLowerCase()) ||
          (c.downlinerName?.toLowerCase() || '').includes(search.toLowerCase()) ||
          c.id.toLowerCase().includes(search.toLowerCase()))
        ).length === 0 && (
          <div className="text-center py-8 text-gray-500">No commission records found.</div>
        )}
      </div>
    </div>
  );
};

// CardView Component
const CardView: React.FC<{ commissions: CommissionRecord[]; search: string; staticFilter: 'all' | 'paid' | 'pending' | 'cancelled' }> = ({ commissions, search, staticFilter }) => {
  const statusColors: Record<string, string> = {
    paid: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {commissions
        .filter(c =>
          (staticFilter === 'all' || c.status === staticFilter) &&
          (c.propertyTitle.toLowerCase().includes(search.toLowerCase()) ||
          c.realtorName.toLowerCase().includes(search.toLowerCase()) ||
          (c.downlinerName?.toLowerCase() || '').includes(search.toLowerCase()) ||
          c.id.toLowerCase().includes(search.toLowerCase()))
        )
        .map(c => (
          <Card key={c.id} className="p-6 shadow-lg bg-white rounded-2xl border-t-4 border-primary hover:shadow-xl transition">
            <div className="flex items-center justify-between mb-2">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[c.status]}`}>
                {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
              </span>
              <span className="text-xs text-gray-400">{new Date(c.date).toLocaleDateString()}</span>
            </div>
            <h2 className="text-lg font-bold text-primary mb-1">{c.propertyTitle}</h2>
            <div className="text-sm text-gray-600 mb-2">Property ID: <span className="font-mono">{c.propertyId}</span></div>
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">💰</span>
              <span className="text-xl font-semibold text-green-700">₦{c.amount.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
              <span>Realtor: <span className="font-medium text-gray-700">{c.realtorName}</span></span>
              <span className="italic">{c.commissionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
            </div>
            <Button variant="primary" className="w-full mt-2">View Details</Button>
          </Card>
        ))}
      {commissions.filter(c =>
        (staticFilter === 'all' || c.status === staticFilter) &&
        (c.propertyTitle.toLowerCase().includes(search.toLowerCase()) ||
        c.realtorName.toLowerCase().includes(search.toLowerCase()) ||
        (c.downlinerName?.toLowerCase() || '').includes(search.toLowerCase()) ||
        c.id.toLowerCase().includes(search.toLowerCase()))
      ).length === 0 && (
        <div className="col-span-full text-center text-gray-500 py-12">No commissions found.</div>
      )}
    </div>
  );
};

const CommissionManagementPage: React.FC = () => {
  const {
    currentUser,
    realtors,
    addCommission,
    updateCommissionStatus,
    loading,
    error,
    commissions,
    fetchCommissions
  } = useRealtorContext();

  const [formData, setFormData] = React.useState({
    realtorId: currentUser?.id || '',
    propertyId: '',
    propertyTitle: '',
    downlinerId: '',
    amount: '',
    commissionType: 'direct_sale' as const,
    status: 'pending' as const,
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [filterStatus, setFilterStatus] = React.useState<'all' | 'pending' | 'paid' | 'cancelled'>('all');
  const [tableSearch, setTableSearch] = React.useState('');
  const [showCardView, setShowCardView] = React.useState(true);

  const staticCommissions: CommissionRecord[] = [
    {
      id: 'C-1001',
      realtorId: 'r1',
      realtorName: 'Gbenga Omoyeni',
      downlinerId: '',
      downlinerName: '',
      commissionType: 'direct_sale',
      propertyId: 'P-001',
      propertyTitle: 'Sunrise Estate, Lekki',
      amount: 250000,
      date: '2025-06-01',
      status: 'paid',
    },
    {
      id: 'C-1002',
      realtorId: 'r2',
      realtorName: 'Ayo Balogun',
      downlinerId: 'r1',
      downlinerName: 'Gbenga Omoyeni',
      commissionType: 'level1_downliner',
      propertyId: 'P-002',
      propertyTitle: 'Greenfield Gardens',
      amount: 180000,
      date: '2025-05-20',
      status: 'pending',
    },
    {
      id: 'C-1003',
      realtorId: 'r3',
      realtorName: 'Chika Okafor',
      downlinerId: '',
      downlinerName: '',
      commissionType: 'direct_sale',
      propertyId: 'P-003',
      propertyTitle: 'Solar Heights',
      amount: 320000,
      date: '2025-05-10',
      status: 'paid',
    },
    {
      id: 'C-1004',
      realtorId: 'r4',
      realtorName: 'Fatima Bello',
      downlinerId: 'r2',
      downlinerName: 'Ayo Balogun',
      commissionType: 'level2_downliner',
      propertyId: 'P-004',
      propertyTitle: 'Unique Gardens',
      amount: 150000,
      date: '2025-04-28',
      status: 'cancelled',
    },
    {
      id: 'C-1005',
      realtorId: 'r1',
      realtorName: 'Gbenga Omoyeni',
      downlinerId: '',
      downlinerName: '',
      commissionType: 'direct_sale',
      propertyId: 'P-005',
      propertyTitle: 'Sunset Villas',
      amount: 210000,
      date: '2025-04-15',
      status: 'paid',
    },
    {
      id: 'C-1006',
      realtorId: 'r5',
      realtorName: 'Ngozi Nwosu',
      downlinerId: 'r3',
      downlinerName: 'Chika Okafor',
      commissionType: 'level1_downliner',
      propertyId: 'P-006',
      propertyTitle: 'Palm City',
      amount: 195000,
      date: '2025-03-30',
      status: 'pending',
    },
    {
      id: 'C-1007',
      realtorId: 'r6',
      realtorName: 'Emeka Obi',
      downlinerId: '',
      downlinerName: '',
      commissionType: 'direct_sale',
      propertyId: 'P-007',
      propertyTitle: 'Royal Court',
      amount: 275000,
      date: '2025-03-15',
      status: 'paid',
    },
    {
      id: 'C-1008',
      realtorId: 'r4',
      realtorName: 'Fatima Bello',
      downlinerId: 'r5',
      downlinerName: 'Ngozi Nwosu',
      commissionType: 'level2_downliner',
      propertyId: 'P-008',
      propertyTitle: 'Emerald Park',
      amount: 160000,
      date: '2025-02-28',
      status: 'cancelled',
    },
  ];

  const [search, setSearch] = React.useState('');
  const [staticFilter, setStaticFilter] = React.useState<'all' | 'paid' | 'pending' | 'cancelled'>('all');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!formData.realtorId) newErrors.realtorId = 'Realtor is required';
    if (!formData.propertyId) newErrors.propertyId = 'Property ID is required';
    if (!formData.propertyTitle) newErrors.propertyTitle = 'Property Title is required';
    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be a positive number';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      await addCommission({
        realtorId: formData.realtorId,
        propertyId: formData.propertyId,
        propertyTitle: formData.propertyTitle,
        downlinerId: formData.downlinerId || undefined,
        amount: Number(formData.amount),
        commissionType: formData.commissionType,
        status: formData.status,
        date: '',
        realtorName: ''
      });
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({
          realtorId: currentUser?.id || '',
          propertyId: '',
          propertyTitle: '',
          downlinerId: '',
          amount: '',
          commissionType: 'direct_sale',
          status: 'pending',
        });
      }, 3000);
    } catch (err) {
      console.error('Failed to add commission:', err);
    }
  };

  const handleStatusUpdate = async (commissionId: string, newStatus: 'pending' | 'paid' | 'cancelled') => {
    try {
     await updateCommissionStatus(commissionId, newStatus);
    } catch (err) {
      console.error('Failed to update commission status:', err);
    }
  };

  const realtorOptions = realtors.map(realtor => ({
    value: realtor.id,
    label: realtor.name
  }));

  const getDownlinerOptions = (selectedRealtorId: string) => {
    return realtors
      .filter(realtor => realtor.id !== selectedRealtorId)
      .map(realtor => ({
        value: realtor.id,
        label: realtor.name
      }));
  };

  const commissionTypeOptions = [
    { value: 'direct_sale', label: 'Direct Sale' },
    { value: 'level1_downliner', label: 'Level 1 Downliner' },
    { value: 'level2_downliner', label: 'Level 2 Downliner' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'paid', label: 'Paid' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Commissions' },
    { value: 'pending', label: 'Pending' },
    { value: 'paid', label: 'Paid' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const getFilteredCommissions = () => {
    let filtered = staticCommissions;
    if (filterStatus !== 'all') {
      filtered = filtered.filter(commission => commission.status === filterStatus);
    }
    return filtered;
  };

  const calculateTotal = (commissionList: CommissionRecord[] | undefined) => {
    if (!commissionList) return 0;
    return commissionList.reduce((total, commission) => {
      if (commission.status !== 'cancelled') {
        return total + commission.amount;
      }
      return total;
    }, 0);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        <div className="bg-primary-dark text-white py-10">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold mb-4">Commission Management</h1>
            <p className="text-xl max-w-2xl mx-auto mb-5">
              Track, manage, and analyze your commission earnings from property sales and affiliate referrals.
            </p>
          </div>
        </div>
        <Section background="light">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <Card className="p-6 mb-6">
                <h2 className="text-xl font-bold text-primary mb-4">Commission Summary</h2>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-gray-600">Total Paid:</span>
                    <span className="text-xl font-bold text-green-600">
                      ₦{calculateTotal(staticCommissions.filter(c => c.status === 'paid')).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-gray-600">Total Pending:</span>
                    <span className="text-xl font-bold" style={{ color: '#fad826' }}>
                      ₦{calculateTotal(staticCommissions.filter(c => c.status === 'pending')).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Cancelled:</span>
                    <span className="text-xl font-bold text-red-600">
                      ₦{calculateTotal(staticCommissions.filter(c => c.status === 'cancelled')).toLocaleString()}
                    </span>
                  </div>
                </div>
              </Card>
              <Card className="p-6">
                <h2 className="text-xl font-bold text-primary mb-4">Record New Commission</h2>
                {error && (
                  <div className="mb-6 p-4 bg-red-100 text-red-800 rounded-lg">
                    {error}
                  </div>
                )}
                {showSuccess && (
                  <div className="mb-6 p-4 bg-green-100 text-green-800 rounded-lg animate-fade-in">
                    Commission recorded successfully!
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  <FormSelect 
                    label="Realtor"
                    name="realtorId"
                    value={formData.realtorId}
                    onChange={handleChange}
                    options={realtorOptions}
                    placeholder="Select realtor"
                    required
                    error={errors.realtorId}
                    className="mb-4"
                  />
                  <FormInput
                    label="Property ID"
                    name="propertyId"
                    type="text"
                    value={formData.propertyId}
                    onChange={handleChange}
                    placeholder="Enter property ID"
                    required
                    error={errors.propertyId}
                    className="mb-4"
                  />
                  <FormInput
                    label="Property Title"
                    name="propertyTitle"
                    type="text"
                    value={formData.propertyTitle}
                    onChange={handleChange}
                    placeholder="Enter property title"
                    required
                    error={errors.propertyTitle}
                    className="mb-4"
                  />
                  <FormSelect
                    label="Commission Type"
                    name="commissionType"
                    value={formData.commissionType}
                    onChange={handleChange}
                    options={commissionTypeOptions}
                    required
                    error={errors.commissionType}
                    className="mb-4"
                  />
                  {formData.commissionType !== 'direct_sale' && (
                    <FormSelect
                      label="Downliner"
                      name="downlinerId"
                      value={formData.downlinerId}
                      onChange={handleChange}
                      options={getDownlinerOptions(formData.realtorId)}
                      placeholder="Select downliner"
                      required
                      error={errors.downlinerId}
                      className="mb-4"
                    />
                  )}
                  <FormInput
                    label="Commission Amount (₦)"
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="Enter amount in Naira"
                    required
                    error={errors.amount}
                    className="mb-4"
                  />
                  <FormSelect
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    options={statusOptions}
                    className="mb-6"
                  />
                  <Button 
                    type="submit"
                    variant="primary"
                    className="w-full"
                  >
                    {loading ? 'Recording...' : 'Record Commission'}
                  </Button>
                </form>
              </Card>
            </div>
            <div className="lg:col-span-2">
              <Card className="p-6 mb-8">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <h2 className="text-xl font-bold text-primary">Commission History</h2>
                    <input
                      type="text"
                      placeholder="Live search by property, realtor, downliner, or ID..."
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary w-full md:w-72"
                      value={tableSearch}
                      onChange={e => setTableSearch(e.target.value)}
                    />
                    <select
                      value={staticFilter}
                      onChange={e => setStaticFilter(e.target.value as any)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="all">All</option>
                      <option value="paid">Paid</option>
                      <option value="pending">Pending</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <ViewToggle showCardView={showCardView} setShowCardView={setShowCardView} />
                </div>
                {showCardView ? (
                  <CardView commissions={staticCommissions} search={tableSearch} staticFilter={staticFilter} />
                ) : (
                  <TableView commissions={staticCommissions} search={tableSearch} staticFilter={staticFilter} />
                )}
              </Card>
             {/*  <Card className="p-6 mb-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                  <h2 className="text-xl font-bold text-primary mb-2 md:mb-0">Searchable Demo Commission Cards</h2>
                  <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-center">
                    <input
                      type="text"
                      placeholder="Search by property, affiliate, or ID..."
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                    <select
                      value={staticFilter}
                      onChange={e => setStaticFilter(e.target.value as any)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="all">All</option>
                      <option value="paid">Paid</option>
                      <option value="pending">Pending</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredStatic.map(c => (
                    <Card key={c.id} className="p-6 shadow-lg bg-white rounded	 rounded-2xl border-t-4 border-primary hover:shadow-xl transition">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold 
                          ${statusColors[c.status]}`}>
                          {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                        </span>
                        <span className="text-xs text-gray-400">{new Date(c.date).toLocaleDateString()}</span>
                      </div>
                      <h2 className="text-lg font-bold text-primary mb-1">{c.propertyTitle}</h2>
                      <div className="text-sm text-gray-600 mb-2">Property ID: <span className="font-mono">{c.propertyId}</span></div>
                      <div className="flex items-center mb-2">
                        <span className="text-2xl mr-2">💰</span>
                        <span className="text-xl font-semibold text-green-700">₦{c.amount.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                        <span>Realtor: <span className="font-medium text-gray-700">{c.realtorName}</span></span>
                        <span className="italic">{c.commissionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      </div>
                      <Button variant="primary" className="w-full mt-2">View Details</Button>
                    </Card>
                  ))}
                  {filteredStatic.length === 0 && (
                    <div className="col-span-full text-center text-gray-500 py-12">No commissions found.</div>
                  )}
                </div>
              </Card> */}
            </div>
          </div>
        </Section>
      </main>
      <Footer />
    </div>
  );
};

export default CommissionManagementPage;
